import React, { useEffect, useState, useCallback } from "react";
import { getAllMagzines, getAllMagzinesByMonth } from "../../../service/DashboardService";
import { years } from "../../../functions/YearGenerate";

const sortMagazines = (list) => {
  const monthToNum = (m) => {
    if (!m) return 0;
    const lower = m.toLowerCase();
    if (lower.startsWith("jan")) return 1;
    if (lower.startsWith("feb")) return 2;
    if (lower.startsWith("mar")) return 3;
    if (lower.startsWith("apr")) return 4;
    if (lower.startsWith("may")) return 5;
    if (lower.startsWith("jun")) return 6;
    if (lower.startsWith("jul")) return 7;
    if (lower.startsWith("aug")) return 8;
    if (lower.startsWith("sep")) return 9;
    if (lower.startsWith("oct")) return 10;
    if (lower.startsWith("nov")) return 11;
    if (lower.startsWith("dec")) return 12;
    return 0;
  };

  return [...list].sort((a, b) => {
    const timeA = a.UploadDataTime ? new Date(a.UploadDataTime).getTime() : 0;
    const timeB = b.UploadDataTime ? new Date(b.UploadDataTime).getTime() : 0;
    if (timeA && timeB && timeA !== timeB) {
      return timeB - timeA;
    }
    const yearA = parseInt(a.Year || a.MagazineYear || 0, 10);
    const yearB = parseInt(b.Year || b.MagazineYear || 0, 10);
    if (yearA !== yearB) {
      return yearB - yearA; // descending
    }
    const monthA = monthToNum(a.Month || a.MagazineMonth);
    const monthB = monthToNum(b.Month || b.MagazineMonth);
    return monthB - monthA; // descending
  });
};

const ViewMag = () => {
  const [magazines, setMagazines] = useState([]);
  const [filteredMagazines, setFilteredMagazines] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedMagazine, setSelectedMagazine] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAllMagazines = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await getAllMagzines(token);
      const sorted = sortMagazines(response.Magazines || response.Result || []);
      setMagazines(sorted);
    } catch (error) {
      console.log("Error loading magazines:", error);
      setMagazines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchByMonth = useCallback(async (month) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await getAllMagzinesByMonth(token, month);
      const sorted = sortMagazines(response.Magazines || response.Result || []);
      setMagazines(sorted);
    } catch (error) {
      console.log("Error loading magazines by month:", error);
      setMagazines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load all magazines on mount
  useEffect(() => {
    fetchAllMagazines();
  }, [fetchAllMagazines]);

  // Filter by month when month is selected
  useEffect(() => {
    if (selectedMonth) {
      fetchByMonth(selectedMonth);
    } else {
      fetchAllMagazines();
    }
  }, [selectedMonth, fetchAllMagazines, fetchByMonth]);

  // Handle year filtering and auto-selection of latest
  useEffect(() => {
    const filtered = magazines.filter((mag) => {
      if (!selectedYear) return true;
      const magYear = mag.Year || mag.MagazineYear;
      return String(magYear) === String(selectedYear);
    });
    setFilteredMagazines(filtered);
    if (filtered.length > 0) {
      setSelectedMagazine(filtered[0].MagazineFile);
    } else {
      setSelectedMagazine(null);
    }
  }, [magazines, selectedYear]);

  const extractFileName = (filePath) => {
    if (!filePath) return "Magazine";
    const parts = filePath.split("/");
    return parts[parts.length - 1];
  };

  const formatMagazineName = (magazine) => {
    let month = magazine.Month || magazine.MagazineMonth;
    let year = magazine.Year || magazine.MagazineYear;

    if (!month || !year) {
      let fileName = extractFileName(magazine.MagazineFile);
      if (fileName && fileName !== "NA") {
        try {
          fileName = decodeURIComponent(fileName);
        } catch (e) {}
        const parts = fileName.split(".");
        if (parts[0] && /^\d{4}$/.test(parts[0])) {
          year = parts[0];
          if (parts[1]) {
            month = parts[1];
          }
        }
      }
    }

    if (month && year) {
      let cleanMonth = month;
      try {
        cleanMonth = decodeURIComponent(month);
      } catch (e) {
        cleanMonth = month.replace(/%26/g, "&");
      }
      cleanMonth = cleanMonth.charAt(0).toUpperCase() + cleanMonth.slice(1);
      return `${cleanMonth} / ${year}`;
    }

    return magazine.MagazineTitle || extractFileName(magazine.MagazineFile);
  };

  const handleMagazineSelect = (magazineFile) => {
    setSelectedMagazine(magazineFile);
  };

  const months = [
    { label: "January", value: "january" },
    { label: "February", value: "february" },
    { label: "March", value: "march" },
    { label: "April", value: "april" },
    { label: "May", value: "may" },
    { label: "June", value: "june" },
    { label: "July", value: "july" },
    { label: "August", value: "august" },
    { label: "September", value: "september" },
    { label: "October", value: "october" },
    { label: "November", value: "november" },
    { label: "December", value: "december" },
  ];

  return (
    <>
      <div className="min-h-full flex gap-4 mb-10">
        <aside className="w-60">
          <div className="h-full flex flex-col gap-5 px-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full border-2 border-gray-400 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
              >
                <option className="font-normal" value="">
                  All Years
                </option>
                {years.map((yr, i) => (
                  <option key={i} value={yr.value}>
                    {yr.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full border-2 border-gray-400 rounded-md py-2 px-3 shadow-sm focus:outline-none focus:border-blue-500"
              >
                <option className="font-normal" value="">
                  All Months
                </option>
                {months.map((mon, i) => (
                  <option key={i} value={mon.value}>
                    {mon.label}
                  </option>
                ))}
              </select>
            </div>
            {/* Magazine list in sidebar */}
            {!loading && filteredMagazines.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Available Magazines</h3>
                <div className="flex flex-col gap-2">
                  {filteredMagazines.map((magazine, idx) => (
                    <button
                      key={magazine.MagazineId || idx}
                      onClick={() => handleMagazineSelect(magazine.MagazineFile)}
                      className={`text-left text-sm py-2 px-3 rounded-md transition-colors ${
                        selectedMagazine === magazine.MagazineFile
                          ? "bg-gray-800 text-white"
                          : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                      }`}
                    >
                      {formatMagazineName(magazine)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>
        <div className="flex-1">
          <div className="mx-auto max-w-10xl bg-gray-100 border-2 border-dashed border-gray-800 relative">
            {selectedMagazine ? (
              <>
                <div className="flex items-center justify-between bg-gray-800 text-white px-4 py-2">
                  <span className="text-sm font-medium">
                    {(() => {
                      const selectedMagObject = magazines.find(m => m.MagazineFile === selectedMagazine);
                      return selectedMagObject ? formatMagazineName(selectedMagObject) : extractFileName(selectedMagazine);
                    })()}
                  </span>
                  <button
                    onClick={() => setSelectedMagazine(null)}
                    className="text-sm underline hover:text-gray-300"
                  >
                    ← Back to list
                  </button>
                </div>
                <iframe
                  src={selectedMagazine}
                  title="PDF Viewer"
                  className="min-h-screen w-full"
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-screen p-8">
                {loading ? (
                  <p className="text-gray-500">Loading magazines...</p>
                ) : filteredMagazines.length === 0 ? (
                  <div className="text-center">
                    <p className="text-gray-500 text-lg mb-2">No magazines found</p>
                    <p className="text-gray-400 text-sm">
                      {selectedMonth
                        ? "Try selecting a different month or clear filters."
                        : "No magazines have been uploaded yet."}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                    {filteredMagazines.map((magazine, idx) => (
                      <button
                        key={magazine.MagazineId || idx}
                        onClick={() => handleMagazineSelect(magazine.MagazineFile)}
                        className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-4 px-6 rounded-md transition-colors text-center flex flex-col items-center justify-center"
                      >
                        <p className="text-sm font-normal mb-1">
                          Magazine
                        </p>
                        <p className="text-lg">
                          {formatMagazineName(magazine)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewMag;
